import { GET, POST, PATCH, DELETE } from "../config";

/**
 * =================================
 * CATEGORÍAS DE GASTOS
 * =================================
 */

export const listExpenseCategories = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const url = params.toString()
    ? `finance/expense-categories/?${params.toString()}`
    : `finance/expense-categories/`;

  const response = await GET(url);
  return response.data;
};

export const createExpenseCategory = async (data) => {
  const response = await POST("finance/expense-categories/", data);
  return response.data;
};

export const updateExpenseCategory = async (id, data) => {
  const response = await PATCH(`finance/expense-categories/${id}/`, data);
  return response.data;
};

export const deleteExpenseCategory = async (id) => {
  const response = await DELETE(`finance/expense-categories/${id}/`);
  return response.data;
};

/**
 * =================================
 * GASTOS FIJOS (FIXED EXPENSES)
 * =================================
 */

export const listFixedExpenses = async ({
  page = 1,
  branch = null,
  category = null,
  status = null,
  frequency = null,
  page_size = 20, // ✅ Unificado con frontend default
} = {}) => {
  // ✅ Validar que page_size sea un número válido
  const validPageSize = Math.max(1, Math.min(200, parseInt(page_size) || 20));
  if (page_size !== validPageSize) {
    console.warn(`[listFixedExpenses] page_size ajustado de ${page_size} a ${validPageSize}`);
  }

  const params = new URLSearchParams();
  params.append("page", page);
  params.append("page_size", validPageSize);

  if (branch) params.append("branch", branch);
  if (category) params.append("category", category);
  if (status) params.append("status", status);
  if (frequency) params.append("frequency", frequency);

  const response = await GET(`finance/fixed-expenses/?${params.toString()}`);
  return response.data;
};

export const getFixedExpense = async (id) => {
  const response = await GET(`finance/fixed-expenses/${id}/`);
  return response.data;
};

export const createFixedExpense = async (data) => {
  const response = await POST("finance/fixed-expenses/", data);
  return response.data;
};

export const updateFixedExpense = async (id, data) => {
  const response = await PATCH(`finance/fixed-expenses/${id}/`, data);
  return response.data;
};

export const deleteFixedExpense = async (id) => {
  const response = await DELETE(`finance/fixed-expenses/${id}/`);
  return response.data;
};

/**
 * =================================
 * ACCIONES ESPECÍFICAS DE GASTOS
 * =================================
 */

export const getActiveExpenses = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const response = await GET(`finance/fixed-expenses/active/?${params.toString()}`);
  return response.data;
};

export const getExpensesSummary = async ({ branch = null, start_date = null, end_date = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);

  const response = await GET(`finance/fixed-expenses/summary/?${params.toString()}`);
  return response.data;
};

export const updateExpenseStatus = async (id, status) => {
  const response = await POST(`finance/fixed-expenses/${id}/update_status/`, {
    status,
  });
  return response.data;
};

export const getExpensesByFrequency = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const response = await GET(
    `finance/fixed-expenses/by_frequency/?${params.toString()}`
  );
  return response.data;
};

/**
 * =================================
 * PAGOS DE GASTOS (usando Payment unificado)
 * =================================
 */

export const addExpensePayment = async (expenseId, paymentData) => {
  const response = await POST(
    `finance/fixed-expenses/${expenseId}/add_payment/`,
    paymentData
  );
  return response.data;
};

export const getExpensePayments = async (expenseId) => {
  const response = await GET(`finance/fixed-expenses/${expenseId}/payments_list/`);
  return response.data;
};

export const convertExpenseToRevenue = async (expenseId, data = {}) => {
  const response = await POST(`finance/fixed-expenses/${expenseId}/convert_to_revenue/`, data);
  return response.data;
};

/**
 * =================================
 * COMPROBANTES DE EGRESOS
 * =================================
 */

/**
 * Exportar egresos a Excel
 */
export const exportToExcel = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      queryParams.append(key, params[key]);
    }
  });

  // Usar GET con responseType blob para descargar archivos
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  const response = await Axios.get(
    `finance/fixed-expenses/export-excel/?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: "blob",
    }
  );
  return response;
};

/**
 * Descargar comprobante de egreso
 * Si hay archivo adjunto, lo descarga. Si no, genera PDF automáticamente.
 * GET /api/finance/fixed-expenses/{id}/download-voucher/
 */
export const downloadExpenseVoucher = async (id) => {
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  try {
    const response = await Axios.get(`finance/fixed-expenses/${id}/download-voucher/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: 'blob',
    });
    
    // Verificar si la respuesta es realmente un PDF o un error JSON
    // Los errores del backend con responseType: 'blob' vienen como JSON dentro de un Blob
    if (response.data instanceof Blob) {
      // Verificar el Content-Type de la respuesta
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Es un error JSON, leerlo como texto
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al descargar comprobante');
      }
      // Es un PDF válido
      return response.data;
    }
    
    return response.data;
  } catch (error) {
    // Si es un error de axios con respuesta blob que es JSON
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al descargar comprobante');
      } catch (parseError) {
        // Si no se puede parsear, usar el mensaje del error original
        const errorMsg = error.message || 'Error al descargar comprobante';
        throw new Error(errorMsg);
      }
    }
    // Si el error tiene un mensaje, usarlo
    if (error.message) {
      throw error;
    }
    throw new Error('Error al descargar comprobante');
  }
};

/**
 * =================================
 * CONSTANTES
 * =================================
 */

export const EXPENSE_CATEGORY_TYPES = {
  RENT: "Renta",
  UTILITIES: "Servicios Públicos",
  SALARIES: "Salarios",
  INSURANCE: "Seguros",
  MAINTENANCE: "Mantenimiento",
  MARKETING: "Marketing",
  LICENSES: "Licencias",
  EQUIPMENT: "Equipos",
  SUPPLIES: "Suministros",
  OTHER: "Otros",
};

export const EXPENSE_FREQUENCIES = {
  MONTHLY: "Mensual",
  QUARTERLY: "Trimestral",
  SEMI_ANNUAL: "Semestral",
  ANNUAL: "Anual",
  ONE_TIME: "Una vez",
};

export const EXPENSE_STATUSES = {
  ACTIVE: "Activo",
  INACTIVE: "Inactivo",
  PENDING: "Pendiente",
  CANCELLED: "Cancelado",
};

/**
 * =================================
 * ANULAR/CANCELAR EGRESOS
 * =================================
 */

export const cancelExpense = async (id, { reason = "" } = {}) => {
  const response = await POST(`finance/fixed-expenses/${id}/cancel/`, { reason });
  return response.data;
};
